import { ReactNode } from "react";
import { motion } from "framer-motion";

interface SectionProps {
  children: ReactNode;
  dark?: boolean;
  offWhite?: boolean;
  className?: string;
  id?: string;
}

const Section = ({ children, dark, offWhite, className = "", id }: SectionProps) => (
  <section
    id={id}
    className={`py-24 md:py-32 ${
      dark ? "bg-navy-deep text-primary-foreground" : offWhite ? "bg-off-white" : "bg-background"
    } ${className}`}
  >
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10">{children}</div>
  </section>
);

const SectionLabel = ({ children }: { children: string }) => (
  <motion.div
    initial={{ opacity: 0, x: -20 }}
    whileInView={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.5 }}
    viewport={{ once: true }}
    className="section-label mb-5"
  >
    {children}
  </motion.div>
);

const SectionTitle = ({ children, light }: { children: ReactNode; light?: boolean }) => (
  <motion.h2
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, delay: 0.1 }}
    viewport={{ once: true }}
    className={`font-display text-[clamp(2rem,4.5vw,3.2rem)] leading-[1.1] tracking-tight mb-8 ${
      light ? "text-primary-foreground" : "text-navy"
    }`}
  >
    {children}
  </motion.h2>
);

const FadeIn = ({ children, delay = 0, className = "" }: { children: ReactNode; delay?: number; className?: string }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.7, delay, ease: "easeOut" }}
    viewport={{ once: true }}
    className={className}
  >
    {children}
  </motion.div>
);

export { Section, SectionLabel, SectionTitle, FadeIn };
